import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import categoriaGruposApi from '../../services/categoriaGruposApi.js';
import { colors } from '../../utils/colors.js';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.gray[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Nome = styled.span`
  font-weight: 500;
  color: ${colors.gray[900]};
`;

const Descricao = styled.span`
  font-size: 13px;
  color: ${colors.gray[500]};
`;

const StatusBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => (props.$status === 'active' ? colors.success.light : colors.warning.light)};
  color: ${(props) => (props.$status === 'active' ? colors.success.dark : colors.warning.dark)};
`;

const OrdemBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${colors.gray[100]};
  color: ${colors.gray[600]};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.error.main : colors.gray[500])};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${(props) => (props.$danger ? colors.error.dark : colors.primary[600])};
  }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.form`
  background: ${colors.white};
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  resize: vertical;
  min-height: 70px;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const emptyForm = { titulo: '', descricao: '', status: 'active', ordem: 0 };

export default function CategoriaGrupoManager() {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // app_admin só gerencia grupos globais (space_id null); sem filtro de
      // space_id o backend retorna todos os grupos, filtramos os globais aqui.
      const data = await categoriaGruposApi.list();
      const globais = (data || []).filter((g) => !g.space_id);
      setGrupos(globais);
    } catch (err) {
      // silencioso: a lista simplesmente fica vazia
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (grupo) => {
    setEditing(grupo);
    setForm({
      titulo: grupo.titulo,
      descricao: grupo.descricao || '',
      status: grupo.status || 'active',
      ordem: grupo.ordem ?? 0,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (grupo) => {
    if (!window.confirm(`Remover o grupo "${grupo.titulo}"?`)) return;
    try {
      await categoriaGruposApi.remove(grupo.id);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível remover o grupo.');
    }
  };

  const handleToggleStatus = async (grupo) => {
    const novoStatus = (grupo.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await categoriaGruposApi.update(grupo.id, { status: novoStatus });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar o status do grupo.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const payload = {
      titulo: form.titulo,
      descricao: form.descricao || null,
      status: form.status,
      ordem: Number(form.ordem) || 0,
      space_id: null,
    };
    try {
      if (editing) {
        await categoriaGruposApi.update(editing.id, payload);
      } else {
        await categoriaGruposApi.create(payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Não foi possível salvar o grupo.');
    }
  };

  return (
    <div>
      <Header>
        <Title>Grupos de Categoria</Title>
        <AddButton type="button" onClick={openCreate}>
          <FaPlus /> Novo grupo
        </AddButton>
      </Header>

      <List>
        {loading && <Empty>Carregando...</Empty>}
        {!loading && grupos.length === 0 && <Empty>Nenhum grupo cadastrado ainda.</Empty>}
        {!loading &&
          grupos.map((grupo) => {
            const statusValue = grupo.status || 'active';
            return (
              <Row key={grupo.id}>
                <RowLeft>
                  <RowTop>
                    <Nome>{grupo.titulo}</Nome>
                    <StatusBadge $status={statusValue}>
                      {statusValue === 'active' ? 'ativo' : 'inativo'}
                    </StatusBadge>
                    <OrdemBadge>Ordem: {grupo.ordem ?? 0}</OrdemBadge>
                  </RowTop>
                  {grupo.descricao && <Descricao>{grupo.descricao}</Descricao>}
                </RowLeft>
                <Actions>
                  <IconButton
                    type="button"
                    onClick={() => handleToggleStatus(grupo)}
                    title={statusValue === 'active' ? 'Inativar' : 'Ativar'}
                  >
                    {statusValue === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                  </IconButton>
                  <IconButton type="button" onClick={() => openEdit(grupo)} title="Editar">
                    <FaEdit />
                  </IconButton>
                  <IconButton type="button" $danger onClick={() => handleDelete(grupo)} title="Remover">
                    <FaTrash />
                  </IconButton>
                </Actions>
              </Row>
            );
          })}
      </List>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <ModalTitle>{editing ? 'Editar grupo' : 'Novo grupo'}</ModalTitle>

            {formError && <ErrorBox>{formError}</ErrorBox>}

            <Label htmlFor="titulo">Título</Label>
            <Input
              id="titulo"
              value={form.titulo}
              onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
              required
              autoFocus
            />

            <Label htmlFor="descricao">Descrição</Label>
            <TextArea
              id="descricao"
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            />

            <Label htmlFor="status">Status</Label>
            <Select
              id="status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </Select>

            <Label htmlFor="ordem">Ordem</Label>
            <Input
              id="ordem"
              type="number"
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: e.target.value }))}
            />

            <ModalActions>
              <CancelButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </CancelButton>
              <SaveButton type="submit">Salvar</SaveButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
}
